import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'

interface IntegrationsData {
  zapierWebhookUrl: string
  customWebhookUrl: string
  webhookSecret: string
  hubspotApiKey: string
  features: {
    webhooks: boolean
    hubspot: boolean
  }
}

export const Integrations: React.FC = () => {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error'>('success')
  const [showHubSpotKey, setShowHubSpotKey] = useState(false)

  const [form, setForm] = useState({
    zapierWebhookUrl: '',
    customWebhookUrl: '',
    webhookSecret: '',
    hubspotApiKey: '',
  })

  // Fetch current integration settings
  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await api.get('/integrations')
      return response.data.data as IntegrationsData
    },
  })

  // Fill the form when data loads (hubspotApiKey arrives masked)
  useEffect(() => {
    if (data) {
      setForm({
        zapierWebhookUrl: data.zapierWebhookUrl || '',
        customWebhookUrl: data.customWebhookUrl || '',
        webhookSecret: data.webhookSecret || '',
        hubspotApiKey: data.hubspotApiKey || '',
      })
    }
  }, [data])

  const webhooksEnabled = data?.features?.webhooks !== false
  const hubspotEnabled = data?.features?.hubspot !== false

  const updateMutation = useMutation({
    mutationFn: async (updates: any) => {
      const response = await api.put('/integrations', updates)
      return response.data
    },
    onSuccess: () => {
      showMessage(t('integrations.saveSuccess'), 'success')
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('integrations.saveError'), 'error')
    },
  })

  const testMutation = useMutation({
    mutationFn: async (type: 'zapier' | 'custom' | 'hubspot') => {
      const response = await api.post('/integrations/test', { type })
      return response.data
    },
    onSuccess: () => {
      showMessage(t('integrations.testSuccess'), 'success')
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('integrations.testError'), 'error')
    },
  })

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg)
    setMessageType(type)
    setTimeout(() => setMessage(''), 5000)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!data) return

    // Send only changed fields; never resend the masked HubSpot key
    const updates: any = {}
    if (webhooksEnabled) {
      if (form.zapierWebhookUrl !== (data.zapierWebhookUrl || '')) updates.zapierWebhookUrl = form.zapierWebhookUrl
      if (form.customWebhookUrl !== (data.customWebhookUrl || '')) updates.customWebhookUrl = form.customWebhookUrl
      if (form.webhookSecret !== (data.webhookSecret || '')) updates.webhookSecret = form.webhookSecret
    }
    if (hubspotEnabled && form.hubspotApiKey !== (data.hubspotApiKey || '')) {
      updates.hubspotApiKey = form.hubspotApiKey
    }

    if (Object.keys(updates).length === 0) {
      showMessage(t('integrations.noChanges'), 'error')
      return
    }

    updateMutation.mutate(updates)
  }

  const inputClass = 'w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-base text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4] disabled:opacity-50 disabled:cursor-not-allowed'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('integrations.loading')}</div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('integrations.title')}</h1>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          messageType === 'success'
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-8">
          {/* Zapier */}
          <div className="relative bg-[#12121a] border border-white/10 rounded-xl p-6 overflow-hidden">
            <img
              src="/logo-zapier.svg"
              alt=""
              className="absolute top-6 right-6 w-36 h-auto opacity-30 pointer-events-none select-none"
            />
            <div className="relative z-10">
              <h2 className="text-xl font-semibold mb-2">{t('integrations.zapierTitle')}</h2>
              <p className="text-sm text-gray-400 mb-6">{t('integrations.zapierDesc')}</p>
              {!webhooksEnabled && (
                <div className="mb-4 p-3 rounded-lg text-sm bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                  {t('integrations.upgradeHint')}{' '}
                  <Link to="/billing" className="underline font-semibold hover:opacity-80">
                    {t('integrations.upgradeLink')}
                  </Link>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('integrations.zapierWebhookUrl')}</label>
                <input
                  type="url"
                  value={form.zapierWebhookUrl}
                  onChange={(e) => setForm({ ...form, zapierWebhookUrl: e.target.value })}
                  className={inputClass}
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  disabled={!webhooksEnabled}
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  disabled={!webhooksEnabled || !form.zapierWebhookUrl || testMutation.isPending}
                  onClick={() => testMutation.mutate('zapier')}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testMutation.isPending && testMutation.variables === 'zapier' ? t('integrations.testSending') : t('integrations.test')}
                </button>
              </div>
            </div>
          </div>

          {/* Custom webhook */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">{t('integrations.customWebhookTitle')}</h2>
            <p className="text-sm text-gray-400 mb-6">{t('integrations.customWebhookDesc')}</p>
            {!webhooksEnabled && (
              <div className="mb-4 p-3 rounded-lg text-sm bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                {t('integrations.upgradeHint')}{' '}
                <Link to="/billing" className="underline font-semibold hover:opacity-80">
                  {t('integrations.upgradeLink')}
                </Link>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('integrations.customWebhookUrl')}</label>
                <input
                  type="url"
                  value={form.customWebhookUrl}
                  onChange={(e) => setForm({ ...form, customWebhookUrl: e.target.value })}
                  className={inputClass}
                  placeholder="https://example.com/webhook"
                  disabled={!webhooksEnabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('integrations.webhookSecret')}</label>
                <input
                  type="text"
                  value={form.webhookSecret}
                  onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                  className={inputClass}
                  placeholder={t('integrations.webhookSecretPlaceholder')}
                  disabled={!webhooksEnabled}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                disabled={!webhooksEnabled || !form.customWebhookUrl || testMutation.isPending}
                onClick={() => testMutation.mutate('custom')}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testMutation.isPending && testMutation.variables === 'custom' ? t('integrations.testSending') : t('integrations.test')}
              </button>
            </div>
          </div>

          {/* HubSpot */}
          <div className="relative bg-[#12121a] border border-white/10 rounded-xl p-6 overflow-hidden">
            <img
              src="/logo-hubspot.svg"
              alt=""
              className="absolute top-6 right-6 w-36 h-auto opacity-30 pointer-events-none select-none"
            />
            <div className="relative z-10">
              <h2 className="text-xl font-semibold mb-2">{t('integrations.hubspotTitle')}</h2>
              <p className="text-sm text-gray-400 mb-6">{t('integrations.hubspotDesc')}</p>
              {!hubspotEnabled && (
                <div className="mb-4 p-3 rounded-lg text-sm bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                  {t('integrations.upgradeHint')}{' '}
                  <Link to="/billing" className="underline font-semibold hover:opacity-80">
                    {t('integrations.upgradeLink')}
                  </Link>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('integrations.hubspotApiKey')}</label>
                <div className="relative">
                  <input
                    type={showHubSpotKey ? 'text' : 'password'}
                    value={form.hubspotApiKey}
                    onChange={(e) => setForm({ ...form, hubspotApiKey: e.target.value })}
                    className={inputClass}
                    placeholder={t('integrations.hubspotApiKeyPlaceholder')}
                    disabled={!hubspotEnabled}
                  />
                  <button
                    type="button"
                    onClick={() => setShowHubSpotKey((v) => !v)}
                    disabled={!hubspotEnabled}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white disabled:opacity-50"
                    aria-label={showHubSpotKey ? t('integrations.hideKey') : t('integrations.showKey')}
                  >
                    <Icon name={showHubSpotKey ? 'eye-slash' : 'eye'} />
                  </button>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  disabled={!hubspotEnabled || !form.hubspotApiKey || testMutation.isPending}
                  onClick={() => testMutation.mutate('hubspot')}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testMutation.isPending && testMutation.variables === 'hubspot' ? t('integrations.testSending') : t('integrations.test')}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-6 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending ? t('integrations.saving') : t('integrations.saveChanges')}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
